// src/components/superadmin/ClientSetupForm.jsx
// Admin users are added after creation via Configure → Admin Users.
import React, { useState } from 'react';
import { createOrganisation, MODULES } from '../../api/superAdminApi';

const generateIdSuggestion = (name) =>
  name.toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .trim();

function ClientSetupForm({ onClientCreated, onCancel }) {
  const [formData, setFormData] = useState({
    orgName: '',
    orgId: '',
    status: 'active',
    modules: {
      programmes: true,
      community: false,
      supportmgmt: false,
      events: false,
      communications: false,
      reports: false,
      donors: false,
      bespokeai: false,
    },
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleModuleChange = (moduleId) => {
    setFormData(prev => ({
      ...prev,
      modules: { ...prev.modules, [moduleId]: !prev.modules[moduleId] },
    }));
  };

  const handleOrgNameChange = (e) => {
    const orgName = e.target.value;
    setFormData(prev => ({ ...prev, orgName, orgId: generateIdSuggestion(orgName) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!formData.orgName.trim()) {
        throw new Error('Organisation name is required');
      }
      if (!formData.orgId.trim()) {
        throw new Error('Org ID is required');
      }
      if (!/^[a-z0-9-]+$/.test(formData.orgId)) {
        throw new Error('Org ID must contain only lowercase letters, numbers, and hyphens');
      }

      await createOrganisation({
        orgName: formData.orgName.trim(),
        orgId: formData.orgId,
        status: formData.status,
        enabledModules: formData.modules,
      });
      onClientCreated();
    } catch (err) {
      console.error('Error creating client:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const idPreview = formData.orgId || 'wicare';

  return (
    <div className="client-setup-form">
      <div className="form-header">
        <h2>Create New Client Configuration</h2>
        <button type="button" onClick={onCancel} className="close-button">×</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>Basic Information</h3>

          <div className="form-group">
            <label>Organisation Name *</label>
            <input
              type="text"
              name="orgName"
              value={formData.orgName}
              onChange={handleOrgNameChange}
              required
              placeholder="e.g., WiCare"
            />
          </div>

          <div className="form-group">
            <label>Org ID *</label>
            <input
              type="text"
              name="orgId"
              value={formData.orgId}
              onChange={handleInputChange}
              required
              placeholder="e.g., wicare"
            />
            <div className="form-help">
              This will be used as the document ID in both 'clients' and 'organizations' collections.<br/>
              Admin URL: {window.location.origin}/BespokeBridge/{idPreview}/admin/<br/>
              Member URL: {window.location.origin}/BespokeBridge/{idPreview}/member/<br/>
              Volunteer URL: {window.location.origin}/BespokeBridge/{idPreview}/volunteer/
            </div>
          </div>

          <div className="form-group">
            <label>Status *</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>

        <div className="form-section">
          <h3>Module Access</h3>
          <div className="modules-grid">
            <div className="module-item">
              <input type="checkbox" id="module-core" checked disabled />
              <label htmlFor="module-core">
                <strong>Core Module</strong>
                <span>Branding, about us, contact, images, typography and custom CSS (Always enabled)</span>
              </label>
            </div>

            {MODULES.map(m => (
              <div className="module-item" key={m.id}>
                <input
                  type="checkbox"
                  id={`module-${m.id}`}
                  checked={!!formData.modules[m.id]}
                  onChange={() => handleModuleChange(m.id)}
                />
                <label htmlFor={`module-${m.id}`}>
                  <strong>{m.label}</strong>
                  <span>{m.description}</span>
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="form-section">
          <div className="info-box">
            <h4>ℹ️ Admin Users</h4>
            <p>Admin users can be added after client creation via the <strong>Configure → Admin Users</strong> tab.</p>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="cancel-button">
            Cancel
          </button>
          <button type="submit" disabled={loading} className="submit-button">
            {loading ? 'Creating Client...' : 'Create Client'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ClientSetupForm;
